import { Check } from "lucide-react";
import { useTranslation } from "react-i18next";
import { localizedProps } from "src/utils/localize";
import { serviceDetails } from "src/utils/whatsapp";
import CtaButton from "../../components/cta-button";

// Карточка услуги в том виде, в каком её видит клиент.
// Живёт отдельно, потому что её же показывает предпросмотр в админке.
export const ServiceCard = ({ service, lang }) => {
  const { t } = useTranslation();
  const name = localizedProps(service?.name, lang);
  const description = localizedProps(service?.description, lang);
  const features = service?.features || [];
  const priceOptions = service?.priceOptions || [];
  const priceNote = localizedProps(service?.priceNote, lang);
  // Цена словами («Договорная») переводится, число («50 000 ₸») — нет.
  // Если мультиязычное поле заполнено, оно главнее строки `price`.
  const priceText = localizedProps(service?.priceText, lang);

  // Таблица тарифов имеет смысл, только когда в ней есть из чего выбирать.
  // У визы вариант ровно один и его сумма совпадает с ценой услуги — карточка
  // печатала «50 000 ₸» дважды: сверху в таблице и снизу рядом с кнопкой.
  const showPriceTable =
    priceOptions.length > 1 ||
    (priceOptions.length === 1 && priceOptions[0]?.price !== service?.price);

  return (
    // Карточка услуги — только текст: название, что входит, сколько стоит.
    // Фотографии здесь не было смысла: под «оформление визы» или «подбор
    // школы» честного кадра не существует, и любая картинка была бы
    // случайной иллюстрацией, которая отодвигает вниз главное — условия.
    // Фон сплошной: фотополосы за карточками на этой странице больше нет.
    <article className="group flex flex-col justify-between gap-5 sm:gap-6 rounded-2xl border border-kj-line bg-kj-card p-5 sm:p-6 transition-colors hover:border-kj-gold/50">
      <div className="flex flex-col gap-2">
        <h3
          {...name.attrs}
          className="text-lg sm:text-xl font-extrabold uppercase tracking-tight text-kj-ink"
        >
          {name.value}
        </h3>
        <p {...description.attrs} className="text-base text-kj-sub leading-relaxed">
          {description.value}
        </p>

        {/* Что входит в услугу */}
        {features.length > 0 && (
          <ul className="mt-1 flex flex-col gap-1.5">
            {features.map((feature, i) => {
              const item = localizedProps(feature, lang);
              return (
                <li key={i} className="flex items-start gap-2">
                  {/* Тот же нефритовый знак «входит в услугу», что и в
                      списке «включено» на странице тура — один смысл, один цвет */}
                  <Check
                    aria-hidden="true"
                    className="mt-1 h-3.5 w-3.5 flex-shrink-0 text-kj-ok"
                  />
                  <span {...item.attrs} className="text-sm text-kj-sub leading-snug">
                    {item.value}
                  </span>
                </li>
              );
            })}
          </ul>
        )}

        {/* Тарифы: вариант слева, цена справа.
            Фон и рамка те же, что у таблицы цен на странице тура, — блоки
            «сколько это стоит» на сайте должны выглядеть одинаково. */}
        {showPriceTable && (
          <div className="mt-2 rounded-xl border border-kj-line bg-kj-dark/40 p-3 sm:p-4">
            <h4 className="text-xs font-bold uppercase tracking-[0.12em] text-kj-sub">
              {t("services.pricesTitle")}
            </h4>
            <dl className="mt-2 flex flex-col divide-y divide-kj-line">
              {priceOptions.map((option, i) => {
                const label = localizedProps(option?.label, lang);
                return (
                  <div
                    key={i}
                    className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-0.5 py-2 first:pt-0 last:pb-0"
                  >
                    <dt {...label.attrs} className="text-sm text-kj-ink">
                      {label.value}
                    </dt>
                    <dd className="text-sm font-extrabold text-kj-gold whitespace-nowrap">
                      {option?.price}
                    </dd>
                  </div>
                );
              })}
            </dl>
            {priceNote.value && (
              <p
                {...priceNote.attrs}
                className="mt-2.5 text-xs text-kj-sub leading-relaxed"
              >
                {priceNote.value}
              </p>
            )}
          </div>
        )}
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3">
        {/* Цена показывается как есть: у половины услуг это не сумма, а слово
            («Договорная», «По запросу»), и «от Договорная» выглядело сломанным.
            Слово берём из мультиязычного `priceText`, если оно заполнено, —
            иначе на английской версии в строке цены висело русское слово. */}
        <span
          {...(priceText.value ? priceText.attrs : {})}
          className="text-base sm:text-lg font-extrabold text-kj-gold"
        >
          {priceText.value || service?.price || ""}
        </span>
        {/* Подпись кнопки приходит из админки: «Оформить визу», «Подобрать школу».
            Цена уходит в WhatsApp вместе с названием услуги. */}
        <CtaButton
          item={service}
          subject={name.value}
          details={serviceDetails()}
          form={service?.formType === "transfer" ? "transfer" : "basic"}
          variant="outline"
        />
      </div>
    </article>
  );
};

export default ServiceCard;
